import ActBadges from '../../components/ui/ActBadges'
import React, { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { useAiChatRouteSummary } from '../../contexts/AiChatRouteContext'
import CustomDataDisplay from '../../components/CustomDataDisplay'
import EntityComments from '../../components/EntityComments'
import EntityHistory from '../../components/EntityHistory'
import ConfirmModal from '../../components/ConfirmModal'
import { Button, Input } from '../../components/ui'
import Icon from '../../components/Icon'
import { DetailPageHeader, DetailSection, InfoGrid, KpiStrip, ProgressMini } from './detail-pages/DetailPageScaffold'
import StageBudgetTable from './stage-budget/StageBudgetTable'
import { ACT_FILTER_ALL } from './stage-budget/stageBudgetUtils'
import { getStatusActions } from '../../config/contractStatusWorkflow'
import { normalizeDate } from '../../utils/dateUtils'
import { formatRub, formatRubTable, parseAmount } from '../../utils/formatRub'
import './Company.css'

function getStageBudgetTabLabel(stage, idx) {
  const raw = String(stage?.stage_type || stage?.type || stage?.distribution_type || stage?.stage_name || stage?.stage_id || '').trim()
  if (!raw) return `Стадия ${idx + 1}`
  const withoutContractPrefix = raw.includes(' - ')
    ? raw.split(' - ').pop().trim()
    : raw.includes(' — ')
      ? raw.split(' — ').pop().trim()
      : raw
  return withoutContractPrefix.split('_')[0].trim() || withoutContractPrefix
}

function getStageBudgetFullName(stage, idx) {
  return String(stage?.stage_name || stage?.stage_id || stage?.type || `Стадия ${idx + 1}`).trim()
}

function getActAmountAfterTax(act) {
  const afterTax = parseAmount(act?.amount_after_tax)
  return afterTax == null ? (parseAmount(act?.amount) || 0) : afterTax
}

function getActAmountGross(act) {
  const gross = parseAmount(act?.amount)
  return gross == null ? getActAmountAfterTax(act) : gross
}

function buildContractStageBudgetReportData(contract, stage, stageActs) {
  const stageId = String(stage?.stage_id || stage?.id || stage?.stage_name || stage?.type || '').trim()
  const stageType = stage?.stage_type || stage?.type || stage?.distribution_type || getStageBudgetFullName(stage, 0)
  const departments = (stage?.departments || []).map((department) => ({
    ...department,
    teams: (department?.teams && department.teams.length > 0 ? department.teams : [{
      team_id: department?.department_id || department?.department_name,
      team_name: department?.department_name || 'Без команды',
      total_income: department?.total_income,
      total_received: department?.total_received,
      employees: [],
    }]).map((team) => ({
      ...team,
      employees: team?.employees && team.employees.length > 0
        ? team.employees
        : [{
            employee_name: '',
            income: team?.total_income ?? department?.total_income ?? 0,
            received: team?.total_received ?? department?.total_received ?? 0,
            acts_by_act: team?.acts_by_act || {},
          }],
    })),
  }))
  return {
    contract,
    stages: [
      {
        ...stage,
        id: stageId,
        type: stageType,
        distribution_type: stageType,
        cost: stage?.stage_cost ?? stage?.cost ?? stage?.contract_cost ?? 0,
        contract_cost: stage?.contract_cost ?? stage?.stage_cost ?? stage?.cost ?? 0,
      },
    ],
    stages_income: [
      {
        ...stage,
        stage_id: stageId,
        stage_name: stage?.stage_name || stageId,
        departments,
      },
    ],
    acts: stageActs,
  }
}

function getStageActKey(stage) {
  return String(stage?.stage_id || stage?.id || stage?.stage_name || '').trim()
}

function getStageActs(allActs, stage) {
  const stageKey = getStageActKey(stage)
  return (allActs || []).filter((act) => {
    const actStageId = String(act?.stage_id || '').trim()
    return stageKey && actStageId === stageKey
  })
}

function ContractCard() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { setHumanSummary } = useAiChatRouteSummary()
  const [contract, setContract] = useState(null)
  const isAdmin = user?.role === 'admin'
  const canUpdate = user?.permissions?.['contracts.update']
  const canCreateStage = user?.permissions?.['stages.create']
  const canCreateAct = user?.permissions?.['acts.create']
  const [loading, setLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [prepaymentModalOpen, setPrepaymentModalOpen] = useState(false)
  const [prepaymentForm, setPrepaymentForm] = useState({ amount: '', date: '' })
  const [prepaymentSaving, setPrepaymentSaving] = useState(false)
  const [statusChanging, setStatusChanging] = useState(false)
  const [deleteStageModal, setDeleteStageModal] = useState(null)
  const [deletingStage, setDeletingStage] = useState(false)
  const [deleteStageError, setDeleteStageError] = useState(null)
  const [activeStageTab, setActiveStageTab] = useState(null)
  const [activeBudgetActs, setActiveBudgetActs] = useState({})

  usePageHeader({ title: null, hideTopHeader: true }, [])

  useEffect(() => {
    fetchContract()
  }, [id])

  useEffect(() => {
    if (!contract || String(contract.id) !== String(id)) {
      setHumanSummary(null)
      return undefined
    }
    const num = (contract.number || '').trim()
    if (!num) {
      setHumanSummary(null)
      return undefined
    }
    setHumanSummary(`Договор: ${num}`)
    return () => setHumanSummary(null)
  }, [contract, id, setHumanSummary])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // Обработка якоря при загрузке страницы (например, /contract/123#act-456)
    const hash = window.location.hash
    if (hash && contract) {
      const element = document.getElementById(hash.substring(1)) // убираем #
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          // Добавляем визуальное выделение
          element.style.backgroundColor = 'var(--color-secondary-bg)'
          setTimeout(() => {
            element.style.backgroundColor = ''
          }, 2000)
        }, 500) // Небольшая задержка для загрузки контента
      }
    }
  }, [contract])

  useEffect(() => {
    if (!contract || !Array.isArray(contract.stages_income) || contract.stages_income.length === 0) {
      setActiveStageTab(null)
      return
    }
    const availableTabs = contract.stages_income.map((stage, idx) => getStageIncomeTabKey(stage, idx))
    if (!activeStageTab || !availableTabs.includes(activeStageTab)) {
      setActiveStageTab(availableTabs[0])
    }
  }, [contract, activeStageTab])

  const fetchContract = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/company/contracts/${id}`)
      if (response.data && response.data.contract) {
        setContract(response.data.contract)
      }
    } catch (error) {
      console.error('Error fetching contract:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteStage = async (stageId) => {
    try {
      setDeletingStage(true)
      setDeleteStageError(null)
      await api.request({ method: 'DELETE', url: `/company/stages/${encodeURIComponent(stageId)}` })
      setDeleteStageModal(null)
      setDeleteStageError(null)
      fetchContract()
    } catch (error) {
      const msg = error.response?.data?.error || 'Ошибка при удалении стадии'
      setDeleteStageError(msg)
    } finally {
      setDeletingStage(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }

  const getPercent = (plan, actual) => {
    if (!plan || Number(plan) <= 0) return 0
    return Math.round((Number(actual || 0) / Number(plan)) * 100)
  }

  const getPercentTone = (percent) => {
    if (percent >= 90) return 'green'
    if (percent > 0) return 'orange'
    return 'grey'
  }

  const menuIcon = (name) => <Icon name={name} size={14} />

  const getStageIncomeTabKey = (stage, idx) => {
    const rawKey = stage?.stage_id || `income-stage-${idx}`
    const normalized = String(rawKey).replace(/[^a-zA-Z0-9_-]/g, '_')
    return `${normalized}_${idx}`
  }

  const formatStageLabel = (stage) => {
    const rawLabel = String(
      stage?.type
      || stage?.stage_type
      || stage?.distribution_type
      || stage?.name
      || stage?.id
      || '-'
    ).trim()
    const withoutContractPrefix = rawLabel.includes(' - ')
      ? rawLabel.split(' - ').pop().trim()
      : rawLabel
    return withoutContractPrefix.split('_')[0].trim() || withoutContractPrefix || '-'
  }

  const isContractCompleted = (status) => {
    const normalizedStatus = String(status || '').toLowerCase().trim()
    return normalizedStatus.includes('заверш') || normalizedStatus === 'completed'
  }

  const isStageOverdue = (stage) => {
    if (stage?.is_overdue === true) return true
    if (stage?.overdue === true) return true
    if (isContractCompleted(contract?.status)) return false
    const dueSource = stage?.fact_date || stage?.planned_date
    if (!dueSource) return false
    const dueDate = new Date(dueSource)
    if (Number.isNaN(dueDate.getTime())) return false
    dueDate.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return dueDate < today
  }

  const getStageActedAmount = (stage) => {
    const stageId = stage?.id
    return (contract?.acts || []).reduce((sum, act) => {
      if (act.stage_id === stageId) {
        return sum + getActAmountGross(act)
      }
      return sum
    }, 0)
  }

  const getActTitle = (act) => {
    const rawNumber = act?.act_number || act?.number
    if (!rawNumber) return 'Акт'
    const value = String(rawNumber).trim()
    return /^акт/i.test(value) ? value : `Акт №${value}`
  }

  const renderStageCard = (stage) => {
    const overdue = isStageOverdue(stage)
    const acted = getStageActedAmount(stage)
    const cost = Number(stage.cost || 0)
    const progress = cost > 0 ? Math.round((acted / cost) * 100) : 0
    const stageDate = formatDate(stage.planned_date || stage.fact_date)

    return (
      <button
        key={stage.id}
        type="button"
        className={`contract-stage-card ${overdue ? 'contract-stage-card--overdue' : ''}`.trim()}
        onClick={() => navigate(`/stage/${encodeURIComponent(stage.public_id || stage.id)}`)}
      >
        <div className="contract-stage-card__left">
          <div className="contract-stage-card__name">
            <span>{formatStageLabel(stage)}</span>
            {overdue ? <span className="contract-stage-card__overdue" aria-label="Просрочено">⚠</span> : null}
          </div>
          <div className="contract-stage-card__meta">
            <span>
              <Icon name="calendar" size={12} />
              {stageDate}
            </span>
            {overdue ? <span className="contract-stage-card__overdue-label">просрочена</span> : null}
          </div>
          <ProgressMini percent={progress} tone={getPercentTone(progress)} />
        </div>
        <div className="contract-stage-card__right">
          <div className="contract-stage-card__amount">{formatRub(stage.cost)}</div>
          {acted > 0 ? <div className="contract-stage-card__sub">заактировано {formatRub(acted)}</div> : null}
        </div>
        <span className="contract-stage-card__arrow" aria-hidden>›</span>
      </button>
    )
  }

  const buildContractPayload = (overrides = {}) => ({
    number: contract.number,
    client_id: contract.client_id,
    object_id: contract.object_id,
    contract_date: normalizeDate(contract.contract_date) || null,
    total_amount: contract.total_amount,
    status: contract.status,
    budget_status: contract.budget_status || 'новый',
    google_drive_link: contract.google_drive_link || undefined,
    technical_spec_link: contract.technical_spec_link || undefined,
    result_folder_link: contract.result_folder_link || undefined,
    custom_data: contract.custom_data || {},
    prepayment: contract.prepayment,
    prepayment_date: normalizeDate(contract.prepayment_date) || undefined,
    ...overrides
  })

  const CONFIRM_ACTIONS = new Set(['suspend', 'resume', 'complete', 'cancel'])

  const changeStatus = async (targetStatus) => {
    setStatusChanging(true)
    setMessage({ type: '', text: '' })
    try {
      await api.put(`/company/contracts/${id}`, buildContractPayload({ status: targetStatus }))
      await fetchContract()
      setMessage({ type: 'success', text: `Статус изменён на «${targetStatus}»` })
      setTimeout(() => setMessage({ type: '', text: '' }), 4000)
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось изменить статус' })
    } finally {
      setStatusChanging(false)
    }
  }

  const handleStatusAction = (action) => {
    if (action.targetStatus) {
      if (CONFIRM_ACTIONS.has(action.key) && !window.confirm(`Изменить статус на «${action.targetStatus}»?`)) return
      changeStatus(action.targetStatus)
    } else if (action.type === 'navigate') {
      if (action.key === 'approve_budget' || action.key === 'resend_budget_approval') {
        navigate(`/contract/${id}/report-preview?action=approve`)
      } else if (action.key === 'view_approvals') {
        navigate(`/contract/${id}/approvals`)
      }
    } else if (action.type === 'action') {
      if (action.key === 'prepayment') {
        setPrepaymentForm({ amount: contract.prepayment ?? '', date: normalizeDate(contract.prepayment_date) })
        setPrepaymentModalOpen(true)
      }
    }
  }

  const buildHeaderActions = () => {
    const items = []
    if (canUpdate) {
      items.push({
        id: 'edit-contract',
        label: 'Редактировать договор',
        icon: menuIcon('edit'),
        onSelect: () => navigate(`/company/contracts/${id}/edit`)
      })
    }
    items.push(
      {
        id: 'contract-budget-report',
        label: 'Сформировать отчёт по бюджету',
        icon: menuIcon('file'),
        onSelect: () => navigate(`/contract/${id}/report-preview`)
      },
      {
        id: 'export-contract',
        label: 'Экспортировать договор',
        icon: menuIcon('export'),
        onSelect: () => navigate(`/contract/${id}/report-preview`)
      }
    )
    const statusItems = canUpdate
      ? getStatusActions(contract.status, contract.budget_status).map((action) => ({
          id: action.key,
          label: action.label,
          icon: menuIcon(action.icon),
          danger: action.variant === 'danger',
          tone: action.tone,
          disabled: statusChanging,
          onSelect: () => handleStatusAction(action)
        }))
      : []
    if (statusItems.length > 0) {
      items.push({ id: 'contract-status-divider', type: 'divider' }, ...statusItems)
    }
    return items
  }

  const savePrepayment = async () => {
    const amount = prepaymentForm.amount !== '' && prepaymentForm.amount != null ? Number(prepaymentForm.amount) : null
    const date = prepaymentForm.date || null
    setPrepaymentSaving(true)
    try {
      await api.put(`/company/contracts/${id}`, buildContractPayload({
        prepayment: amount,
        prepayment_date: date || undefined
      }))
      await fetchContract()
      setPrepaymentModalOpen(false)
      setPrepaymentForm({ amount: '', date: '' })
      setMessage({ type: 'success', text: 'Предоплата сохранена' })
      setTimeout(() => setMessage({ type: '', text: '' }), 3000)
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось сохранить предоплату' })
      setTimeout(() => setMessage({ type: '', text: '' }), 5000)
    } finally {
      setPrepaymentSaving(false)
    }
  }


  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  if (!contract) {
    return <div className="no-data">Договор не найден</div>
  }

  const actsGrossTotal = (contract.acts || []).reduce((sum, act) => sum + getActAmountGross(act), 0)

  return (
    <div className="company-page contract-card-page entity-detail-page entity-detail-redesign">
      <DetailPageHeader
        breadcrumbs={[
          { label: 'Клиенты', to: '/company/clients' },
          { label: contract.client_name || 'Клиент', to: contract.client_id ? `/client/${contract.client_id}` : undefined },
          { label: contract.number || 'Договор' }
        ]}
        title={contract.number || 'Договор'}
        status={contract.status || 'новый'}
        subtitle={`${contract.client_name || 'Клиент'} — ${contract.object_name || 'Объект'}`}
        onBack={() => navigate('/company/contracts')}
        actions={buildHeaderActions()}
      />

      {message.text && (
        <div className={`message message-${message.type}`} role="alert">
          {message.text}
          <button type="button" className="message-close" onClick={() => setMessage({ type: '', text: '' })} aria-label="Закрыть">×</button>
        </div>
      )}

      <DetailSection title="Паспорт договора">
        <InfoGrid
          items={[
            { label: 'Номер договора', value: contract.number || '-' },
            { label: 'Дата заключения', value: formatDate(contract.contract_date) },
            { label: 'Клиент', value: <button type="button" className="entity-text-link" onClick={() => navigate(`/client/${contract.client_id}`)}>{contract.client_name || '-'}</button> },
            { label: 'Объект', value: <button type="button" className="entity-text-link" onClick={() => navigate(`/object/${contract.object_id}`)}>{contract.object_name || '-'}</button> },
            { label: 'Статус бюджета', value: contract.budget_status || 'новый' },
            { label: 'Предоплата', value: <span className="amount-mono">{formatRub(contract.prepayment)}</span> },
            { label: 'Дата предоплаты', value: formatDate(contract.prepayment_date) },
            { label: 'Внесена предоплата', value: contract.prepayment ? 'Да' : 'Нет' },
            contract.technical_spec_link ? { label: 'Ссылка на ТЗ', value: <a href={contract.technical_spec_link} target="_blank" rel="noopener noreferrer">{contract.technical_spec_link}</a>, full: true } : null,
            contract.google_drive_link ? { label: 'Ссылка на Google Drive', value: <a href={contract.google_drive_link} target="_blank" rel="noopener noreferrer">{contract.google_drive_link}</a>, full: true } : null,
          ]}
        />
      </DetailSection>

      {contract.custom_data && Object.keys(contract.custom_data).length > 0 && (
        <div className="section-card">
          <CustomDataDisplay entityType="contracts" customData={contract.custom_data} />
        </div>
      )}

      <div className="contract-two-col">
        <div className="section-card">
          <div className="section-card-header section-card-header-actions">
            <h2>Бюджеты стадий</h2>
            {canCreateStage && (
              <Button onClick={() => navigate(`/company/contracts/${id}/stages/new`)} variant="primary" className="btn-add">
                + Добавить
              </Button>
            )}
          </div>
          {contract.stages && contract.stages.length > 0 ? (
            <div className={`list-cards${contract.stages.length > 7 ? ' contract-scroll-list' : ''}`}>
                {contract.stages.map((stage) => renderStageCard(stage))}
            </div>
          ) : (
            <div className="contract-stages-empty">
              <Icon name="file" size={40} />
              <p>Стадии не указаны</p>
            </div>
          )}
        </div>

        <div className="section-card">
          <div className="section-card-header section-card-header-actions">
            <h2>Акты</h2>
            {canCreateAct && (
              <Button onClick={() => navigate(`/company/contracts/${id}/acts/new`)} variant="primary" className="btn-add">
                + Добавить акт
              </Button>
            )}
          </div>
          {contract.acts && contract.acts.length > 0 ? (
            <div className={`entity-act-list${contract.acts.length > 7 ? ' contract-scroll-list' : ''}`}>
              {contract.acts.map((act) => (
                <button
                  key={act.id}
                  type="button"
                  className="entity-act-row"
                  onClick={() => navigate(`/act/${act.id}`)}
                >
                  <div className="entity-act-row__main">
                    <div className="entity-act-row__title">{getActTitle(act)}</div>
                    <div className="entity-act-row__meta">{formatDate(act.issue_date)}</div>
                  </div>
                  <div className="entity-act-row__side">
                    <div className="entity-act-row__amount">{formatRub(getActAmountGross(act))}</div>
                    <ActBadges act={act} />
                  </div>
                  <span className="entity-act-row__arrow" aria-hidden>›</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="contract-acts-empty">
              <Icon name="act" size={40} />
              <p>Акты не найдены</p>
            </div>
          )}
        </div>
      </div>

      <KpiStrip
        items={[
          { label: 'Сумма договора', value: formatRub(contract.total_amount), tone: 'accent' },
          { label: 'Заактировано', value: formatRub(actsGrossTotal), tone: 'success' },
          { label: 'Остаток', value: formatRub(Math.max(0, Number(contract.total_amount || 0) - actsGrossTotal)), tone: 'warning' },
          { label: 'Стадии', value: String((contract.stages || []).length), tone: 'info' },
        ]}
      />

      <div className="section-card contract-income-card">
        <div className="section-card-header">
          <h2>Бюджет договора</h2>
        </div>
          {contract.stages_income && contract.stages_income.length > 0 ? (
            <>
              <div className="contract-income-tabs">
                <div className="contract-income-tabs-scroll" role="tablist" aria-label="Стадии доходов">
                  {contract.stages_income.map((stage, stageIdx) => {
                    const tabKey = getStageIncomeTabKey(stage, stageIdx)
                    return (
                      <button
                        key={tabKey}
                        type="button"
                        role="tab"
                        className={`contract-income-tab ${activeStageTab === tabKey ? 'active' : ''}`}
                        aria-selected={activeStageTab === tabKey}
                        aria-controls={`contract-income-panel-${tabKey}`}
                        id={`contract-income-tab-${tabKey}`}
                        onClick={() => setActiveStageTab(tabKey)}
                      >
                        {getStageBudgetTabLabel(stage, stageIdx)}
                      </button>
                    )
                  })}
                </div>
              </div>
              {contract.stages_income.map((stage, stageIdx) => {
                const tabKey = getStageIncomeTabKey(stage, stageIdx)
                const stageActs = getStageActs(contract.acts || [], stage)
                const selectedActId = isAdmin ? (activeBudgetActs[tabKey] || ACT_FILTER_ALL) : ACT_FILTER_ALL
                const stageBudgetReportData = buildContractStageBudgetReportData(contract, stage, stageActs)
                return (
                  <div
                    key={tabKey}
                    role="tabpanel"
                    id={`contract-income-panel-${tabKey}`}
                    aria-labelledby={`contract-income-tab-${tabKey}`}
                    className={`contract-income-panel ${activeStageTab === tabKey ? 'active' : ''}`}
                    hidden={activeStageTab !== tabKey}
                  >
                    <StageBudgetTable
                      reportData={stageBudgetReportData}
                      selectedActId={selectedActId}
                      onSelectedActIdChange={(actId) => setActiveBudgetActs((prev) => ({ ...prev, [tabKey]: actId }))}
                      tableLabel="Бюджет договора"
                      showActFilter={isAdmin}
                    />
                  </div>
                )
              })}
            </>
          ) : (
            <p className="info-note">Данные по доходам не найдены</p>
          )}
      </div>

      <EntityComments entityType="contracts" entityId={id} />
      <EntityHistory entityType="contracts" entityId={id} />
      {prepaymentModalOpen && (
        <div className="modal-overlay" onClick={() => !prepaymentSaving && setPrepaymentModalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <h3>Внесена предоплата</h3>
            <div className="filter-group" style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="prepayment-amount">Сумма предоплаты</label>
              <Input
                id="prepayment-amount"
                type="number"
                value={prepaymentForm.amount}
                onChange={(e) => setPrepaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="filter-group" style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="prepayment-date">Дата предоплаты</label>
              <Input
                id="prepayment-date"
                type="date"
                value={prepaymentForm.date}
                onChange={(e) => setPrepaymentForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => !prepaymentSaving && setPrepaymentModalOpen(false)} disabled={prepaymentSaving}>
                Отмена
              </Button>
              <Button variant="primary" onClick={savePrepayment} disabled={prepaymentSaving}>
                {prepaymentSaving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {deleteStageModal && (
        <ConfirmModal
          title="Удалить стадию?"
          message={<p>Стадия <strong>{deleteStageModal.display_name || deleteStageModal.type || deleteStageModal.id}</strong> будет удалена. Это действие нельзя отменить.</p>}
          confirmLabel="Удалить"
          onConfirm={() => handleDeleteStage(deleteStageModal.id)}
          onCancel={() => { if (!deletingStage) { setDeleteStageModal(null); setDeleteStageError(null); } }}
          confirmDisabled={deletingStage}
          error={deleteStageError}
        />
      )}
    </div>
  )
}

export default ContractCard
